import React, { useContext, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import ModalTest from '../../../../components/BaseModal';
import * as S from '../../../../components/styles/modal-style';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { DreLine, dreLineApi } from '../api';

interface DreLineModalProps {
  /** Ausente cria uma nova linha; presente edita a existente. */
  dreLine?: DreLine | null;
  onClose: () => void;
  onSaved: () => void;
}

const DreLineModal: React.FC<DreLineModalProps> = ({
  dreLine,
  onClose,
  onSaved,
}) => {
  const { setFeedback } = useContext(FeedbackContext);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(true);

  const isEdit = Boolean(dreLine);

  useEffect(() => {
    setName(dreLine?.name ?? '');
    setCode(dreLine?.code ?? '');
    setDescription(dreLine?.description ?? '');
    setStatus(dreLine?.status ?? true);
  }, [dreLine]);

  const handleSave = async () => {
    if (saving) return;

    if (!name.trim()) {
      setFeedback({
        message: 'Informe o nome da linha do DRE.',
        isOpen: true,
        statusOk: false,
      });
      return;
    }

    const payload = {
      name: name.trim(),
      code: code.trim() || undefined,
      description: description.trim() || undefined,
      status,
    };

    try {
      setSaving(true);
      if (dreLine) {
        await dreLineApi.update(dreLine.id, payload);
      } else {
        await dreLineApi.create(payload);
      }
      setFeedback({
        message: isEdit ? 'Linha do DRE atualizada.' : 'Linha do DRE criada.',
        isOpen: true,
        statusOk: true,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setFeedback({
        message:
          err?.response?.data?.message || 'Erro ao salvar a linha do DRE.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalTest>
      <S.ContainerOfModal
        width="560px"
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <S.Header>
          <S.HeaderTitle>
            {isEdit ? 'Editar linha do DRE' : 'Nova linha do DRE'}
          </S.HeaderTitle>
          <S.CloseDiv onClick={onClose}>
            <X size={20} />
          </S.CloseDiv>
        </S.Header>

        <S.FormContainer
          columns="1fr 1fr"
          onSubmit={(event) => {
            event.preventDefault();
            handleSave();
          }}
        >
          <S.InputDiv>
            <S.Label htmlFor="dre-line-name">Nome</S.Label>
            <S.Input
              id="dre-line-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Ex: Receita bruta de vendas"
              maxLength={120}
              autoFocus
            />
          </S.InputDiv>

          <S.InputDiv>
            <S.Label htmlFor="dre-line-code">Código</S.Label>
            <S.Input
              id="dre-line-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Ex: 3.1.01"
              maxLength={40}
            />
          </S.InputDiv>

          <S.InputDiv style={{ gridColumn: '1 / -1' }}>
            <S.Label htmlFor="dre-line-description">Descrição</S.Label>
            <S.Textarea
              id="dre-line-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Descrição da linha"
              maxLength={255}
            />
          </S.InputDiv>

          <S.CheckboxContainer
            as="label"
            style={{ gridColumn: '1 / -1', width: 'fit-content', cursor: 'pointer' }}
          >
            <S.HiddenCheckbox
              checked={status}
              onChange={(event) => setStatus(event.target.checked)}
            />
            <S.StyledCheckbox checked={status} />
            <S.Label style={{ marginLeft: 10, cursor: 'pointer' }}>
              Ativa
            </S.Label>
          </S.CheckboxContainer>

          <S.ButtonContainer>
            <S.Button
              type="button"
              className="cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </S.Button>
            <S.Button type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </S.Button>
          </S.ButtonContainer>
        </S.FormContainer>
      </S.ContainerOfModal>
    </ModalTest>
  );
};

export default DreLineModal;
